import React from 'react';
import { Link } from 'react-router-dom';
import { Crown, CheckCircle, TrendingUp, Shield, Zap } from 'lucide-react';

interface GuestLimitReachedProps {
  feature: 'download' | 'upload';
  onClose?: () => void;
  compact?: boolean;
}

const GuestLimitReached: React.FC<GuestLimitReachedProps> = ({ 
  feature, 
  onClose,
  compact = false 
}) => {
  const featureText = feature === 'download' ? 'YouTube download' : 'video upload';
  
  if (compact) {
    return (
      <div className="bg-gradient-to-br from-gray-50 to-gray-100 border-2 border-gray-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0">
            <div className="w-10 h-10 bg-black rounded-full flex items-center justify-center">
              <Crown className="w-5 h-5 text-white" />
            </div>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">
              🎉 You just used your free {featureText}!
            </h3>
            <p className="text-sm text-gray-600 mb-3">
              Create a <strong>free account</strong> to get more:
            </p>
            <div className="grid grid-cols-2 gap-2 mb-3 text-xs">
              <div className="flex items-center gap-1 text-gray-700">
                <CheckCircle className="w-3 h-3 text-red-500" />
                <span>1 video (lifetime)</span>
              </div>
              <div className="flex items-center gap-1 text-gray-700">
                <CheckCircle className="w-3 h-3 text-red-500" />
                <span>Save your clips</span>
              </div>
              <div className="flex items-center gap-1 text-gray-700">
                <CheckCircle className="w-3 h-3 text-red-500" />
                <span>Track analytics</span>
              </div>
              <div className="flex items-center gap-1 text-gray-700">
                <CheckCircle className="w-3 h-3 text-red-500" />
                <span>Access anywhere</span>
              </div>
            </div>
            <div className="flex gap-2">
              <Link
                to="/signup"
                className="flex-1 bg-black hover:bg-gray-800 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors text-center"
              >
                Create Free Account
              </Link>
              {onClose && (
                <button
                  onClick={onClose}
                  className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Maybe Later
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full mx-auto overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-black to-gray-900 px-6 py-8 text-white text-center">
        <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <Crown className="w-8 h-8" />
        </div>
        <h2 className="text-3xl font-bold mb-2">
          🎉 Great Job!
        </h2>
        <p className="text-lg text-gray-300">
          You just completed your first {featureText}
        </p>
      </div>

      {/* Content */}
      <div className="px-6 py-8">
        <div className="text-center mb-6">
          <p className="text-xl text-gray-700 mb-2">
            Want to do more? <strong>Create a free account</strong> to unlock:
          </p>
          <p className="text-sm text-gray-500">
            No credit card required • Takes 30 seconds
          </p>
        </div>

        {/* Comparison Grid */}
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          {/* Guest Column */}
          <div className="bg-gray-50 rounded-lg p-4 border-2 border-gray-200">
            <div className="text-center mb-3">
              <div className="inline-block bg-gray-200 text-gray-700 text-xs font-semibold px-3 py-1 rounded-full mb-2">
                As Guest
              </div>
            </div>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-center gap-2">
                <span className="text-gray-400">•</span>
                <span>1 {featureText} per day</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-gray-400">•</span>
                <span>No saved history</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-gray-400">•</span>
                <span>No analytics</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-gray-400">•</span>
                <span>Limited features</span>
              </li>
            </ul>
          </div>

          {/* Free Account Column */}
          <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-4 border-2 border-red-300 relative">
            <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg">
              FREE
            </div>
            <div className="text-center mb-3">
              <div className="inline-block bg-black text-white text-xs font-semibold px-3 py-1 rounded-full mb-2">
                With Free Account
              </div>
            </div>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                <span><strong>1 video (lifetime)</strong></span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                <span>Save & organize clips</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                <span>Track performance analytics</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                <span>Access from any device</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                <span>AI-powered clip detection</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Social Proof */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6 text-center">
          <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
            <TrendingUp className="w-4 h-4 text-red-500" />
            <span>Join <strong>10,000+ creators</strong> using ClipThis</span>
          </div>
        </div>

        {/* CTA Buttons */}
        <div className="space-y-3">
          <Link
            to="/signup"
            className="block w-full bg-black hover:bg-gray-800 text-white font-semibold px-6 py-4 rounded-lg transition-colors text-center text-lg shadow-lg hover:shadow-xl"
          >
            Create Free Account
          </Link>
          
          <Link
            to="/login"
            className="block w-full bg-white hover:bg-gray-50 text-gray-700 font-medium px-6 py-3 rounded-lg transition-colors text-center border-2 border-gray-200"
          >
            Already have an account? Sign in
          </Link>

          {onClose && (
            <button
              onClick={onClose}
              className="block w-full text-gray-500 hover:text-gray-700 font-medium px-6 py-2 rounded-lg transition-colors text-center text-sm"
            >
              Maybe later
            </button>
          )}
        </div>

        {/* Trust Badges */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <div className="flex items-center justify-center gap-6 text-xs text-gray-500">
            <div className="flex items-center gap-1">
              <Shield className="w-3 h-3" />
              <span>Secure</span>
            </div>
            <div className="flex items-center gap-1">
              <Zap className="w-3 h-3" />
              <span>Fast Setup</span>
            </div>
            <div className="flex items-center gap-1">
              <CheckCircle className="w-3 h-3" />
              <span>No Credit Card</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuestLimitReached;
